/**
 * Audit retention choices offered to Enterprise tenants.
 *
 * Named by length, not by statute: German HGB §257(5) and AO §147(4) periods
 * (6, 8 or 10 years) run from the END of the calendar year a record is made,
 * so a record can need up to one year more than the stated length. A preset
 * labelled "6 years (HGB)" would keep some records too short. Verified on
 * gesetze-im-internet.de, §257 HGB, 2026-09-30. AO §147(3) also keeps the
 * period open while a tax assessment for the year is open, so an AO record can
 * need longer than any preset; the account copy says so (2026-10-25).
 *
 * Only producer-profile classes (ADCR PRF-01) are offered: the product floor
 * (184 days, REC-20 six-month floor from any start date), then 7 and 11 years
 * (HGB/AO 6- and 10-year periods plus the calendar-year tail). Never-expire
 * and arbitrary custom lengths are refused by the API so every record carries
 * a named profile class.
 *
 * Years are whole days rounded up over leap years (365.25 per year).
 */
export interface RetentionPreset {
  id: string;
  days: number;
  label: string;
}

const yearsToDays = (years: number) => Math.ceil(years * 365.25);

/** Product floor; must match services/api/adcr_record.AUDIT_RETENTION_DAYS. */
export const RETENTION_FLOOR_DAYS = 184;

export const RETENTION_PRESETS: readonly RetentionPreset[] = [
  { id: '184d', days: RETENTION_FLOOR_DAYS, label: '184 days' },
  { id: '7y', days: yearsToDays(7), label: '7 years' },
  { id: '11y', days: yearsToDays(11), label: '11 years' },
];

/** Presets a tenant may pick: only profile classes at or above the floor. */
export function presetsForFloor(floorDays: number): RetentionPreset[] {
  return RETENTION_PRESETS.filter((p) => p.days >= floorDays);
}

/** "7 years" for a preset length, "400 days" otherwise. */
export function retentionPeriodLabel(days: number): string {
  const preset = RETENTION_PRESETS.find((p) => p.days === days);
  return preset ? preset.label : `${days.toLocaleString()} days`;
}
